import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase-config';
import { onAuthStateChanged, updatePassword, sendPasswordResetEmail } from 'firebase/auth';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';

export default function AdvancedUserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState("");
  const [editModalUser, setEditModalUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'user',
    isActive: true,
    emailVerified: false,
    balance: 0
  });
  const [createLoading, setCreateLoading] = useState(false);

  // Monitor auth state and auto-fetch users when ready
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthLoading(false);
      setCurrentUser(user);
      if (user) {
        console.log('✅ User authenticated:', user.email);
        fetchAllUsers();
      } else {
        console.log('❌ No user authenticated');
        setError("Nem vagy bejelentkezve");
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch all users directly from Firestore (bypassing Firebase Function issues)
  const fetchAllUsers = async () => {
    // Check current auth state directly instead of relying on state
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log('❌ No authenticated user at fetch time');
      setError("Nem vagy bejelentkezve");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      console.log('🔍 Fetching ALL users directly from Firestore (bypassing Firebase Function)...');
      console.log('🔐 Current user:', currentUser.email);
      
      const allUsers = [];
      
      // Fetch from 'users' collection
      console.log('📊 Fetching from users collection...');
      const usersSnapshot = await getDocs(collection(db, 'users'));
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        
        // Include ALL users with valid emails (no filtering)
        if (userData.email && userData.email !== 'N/A') {
          allUsers.push({
            id: doc.id,
            fullName: userData.fullName || userData.name || 'Névtelen',
            name: userData.fullName || userData.name || 'Névtelen',
            email: userData.email,
            role: userData.role || 'user',
            createdAt: userData.createdAt || userData.joinedAt || null,
            joinedAt: userData.joinedAt || userData.createdAt || null,
            lastLogin: userData.lastLogin || null,
            phone: userData.phone || 'Nincs telefon',
            balance: userData.balance || 0,
            totalClicks: userData.totalClicks || 0,
            affiliates: userData.affiliates || [],
            partnerCode: userData.partnerCode || userData.referralCode || 'Nincs kód',
            isActive: userData.isActive !== false,
            isBanned: userData.disabled || false,
            emailVerified: userData.emailVerified || false,
            source: 'users'
          });
        }
      });

      // Fetch from 'affiliates' collection
      console.log('📊 Fetching from affiliates collection...');
      const affiliatesSnapshot = await getDocs(collection(db, 'affiliates'));
      affiliatesSnapshot.forEach(doc => {
        const affiliateData = doc.data();
        
        // Include ALL affiliates with valid emails (no filtering)
        if (affiliateData.email && affiliateData.email !== 'N/A') {
          // Check if user already exists by email
          const existingUser = allUsers.find(u => u.email === affiliateData.email);
          if (!existingUser) {
            allUsers.push({
              id: doc.id,
              fullName: affiliateData.fullName || affiliateData.name || 'Névtelen',
              name: affiliateData.fullName || affiliateData.name || 'Névtelen',
              email: affiliateData.email,
              role: affiliateData.role || 'affiliate',
              createdAt: affiliateData.createdAt || affiliateData.joinedAt || null,
              joinedAt: affiliateData.joinedAt || affiliateData.createdAt || null,
              lastLogin: affiliateData.lastLogin || null,
              phone: affiliateData.phone || 'Nincs telefon',
              balance: affiliateData.balance || 0,
              totalClicks: affiliateData.totalClicks || 0,
              affiliates: affiliateData.affiliates || [],
              partnerCode: affiliateData.partnerCode || affiliateData.referralCode || affiliateData.affiliateId || 'Nincs kód',
              isActive: affiliateData.isActive !== false,
              isBanned: affiliateData.disabled || false,
              emailVerified: affiliateData.emailVerified || false,
              source: 'affiliates'
            });
          }
        }
      });
      
      // Sort users by email for consistency
      allUsers.sort((a, b) => a.email.localeCompare(b.email));
      
      console.log(`✅ Loaded ${allUsers.length} users from Firestore collections`);
      console.log('📋 Sample users:', allUsers.slice(0, 5).map(u => ({ 
        email: u.email, 
        name: u.name,
        phone: u.phone,
        registered: u.createdAt ? (u.createdAt.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleDateString('hu-HU') : new Date(u.createdAt).toLocaleDateString('hu-HU')) : 'Nincs dátum',
        verified: u.emailVerified, 
        source: u.source 
      })));
      
      setUsers(allUsers);
    } catch (err) {
      console.error('❌ Error fetching users:', err);
      setError("Nem sikerült betölteni a felhasználókat: " + err.message);
    }
    setLoading(false);
  };

  // Remove the duplicate useEffect that calls fetchAllUsers immediately

  // Handle user selection
  const handleUserSelect = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle bulk actions (direct Firestore approach)
  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;
    
    if (!confirm(`Biztosan végrehajtod ezt a műveletet ${selectedUsers.length} felhasználón?`)) {
      return;
    }

    try {
      for (const userId of selectedUsers) {
        const user = users.find(u => u.id === userId);
        if (!user) continue;

        switch (bulkAction) {
          case 'activate':
          case 'deactivate':
            const isActive = bulkAction === 'activate';
            // Update in both collections
            try {
              await updateDoc(doc(db, 'users', userId), { isActive });
            } catch (e) { /* User not in users collection */ }
            try {
              await updateDoc(doc(db, 'affiliates', userId), { isActive });
            } catch (e) { /* User not in affiliates collection */ }
            break;
            
          case 'verify':
          case 'unverify':
            const emailVerified = bulkAction === 'verify';
            // Update in both collections
            try {
              await updateDoc(doc(db, 'users', userId), { emailVerified });
            } catch (e) { /* User not in users collection */ }
            try {
              await updateDoc(doc(db, 'affiliates', userId), { emailVerified });
            } catch (e) { /* User not in affiliates collection */ }
            break;
            
          case 'delete':
            // Delete from both collections
            try {
              await deleteDoc(doc(db, 'users', userId));
            } catch (e) { /* User not in users collection */ }
            try {
              await deleteDoc(doc(db, 'affiliates', userId));
            } catch (e) { /* User not in affiliates collection */ }
            break;
        }
      }
      
      alert(`Művelet sikeresen végrehajtva ${selectedUsers.length} felhasználón!`);
      setSelectedUsers([]);
      setBulkAction("");
      await fetchAllUsers();
    } catch (error) {
      console.error('Bulk action error:', error);
      alert('Hiba történt a tömeges művelet során: ' + error.message);
    }
  };

  // Handle user edit
  const handleEditUser = (user) => {
    setEditModalUser(user);
    setEditFormData({
      name: user.name || user.fullName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'affiliate',
      isActive: user.isActive !== false,
      emailVerified: user.emailVerified || false,
      balance: user.balance || 0
    });
  };

  // Handle save user edit with Firebase Auth integration
  const handleSaveUserEdit = async (userData) => {
    if (!currentUser) {
      alert('Nem vagy bejelentkezve!');
      return;
    }
    
    setEditLoading(true);
    try {
      // Telefonszám formázás
      const formatPhoneNumber = (phone) => {
        if (!phone) return phone;
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('06')) {
          return '+36' + cleaned.substring(2);
        }
        if (cleaned.startsWith('36')) {
          return '+' + cleaned;
        }
        if (!cleaned.startsWith('36')) {
          return '+36' + cleaned;
        }
        return '+' + cleaned;
      };

      const formattedPhone = formatPhoneNumber(userData.phone);
      
      const updateData = {
        name: userData.name,
        fullName: userData.name,
        email: userData.email,
        phone: formattedPhone,
        role: userData.role,
        isActive: userData.isActive,
        emailVerified: userData.emailVerified,
        balance: parseFloat(userData.balance) || 0,
        updatedAt: new Date()
      };

      // Update in users collection
      try {
        const userRef = doc(db, 'users', editModalUser.id);
        await updateDoc(userRef, updateData);
        console.log('✅ Updated in users collection');
      } catch (error) {
        // If user doesn't exist in users, create it
        try {
          await setDoc(doc(db, 'users', editModalUser.id), {
            ...updateData,
            createdAt: editModalUser.createdAt || new Date()
          });
          console.log('✅ Created in users collection');
        } catch (createError) {
          console.log('Error creating in users collection:', createError);
        }
      }

      // Update in affiliates collection if user is affiliate
      if (userData.role === 'affiliate' || editModalUser.source === 'affiliates') {
        try {
          const affiliateRef = doc(db, 'affiliates', editModalUser.id);
          await updateDoc(affiliateRef, {
            ...updateData,
            affiliateId: editModalUser.id,
            partnerCode: editModalUser.partnerCode || editModalUser.id
          });
          console.log('✅ Updated in affiliates collection');
        } catch (error) {
          // If affiliate doesn't exist, create it
          try {
            await setDoc(doc(db, 'affiliates', editModalUser.id), {
              ...updateData,
              affiliateId: editModalUser.id,
              partnerCode: editModalUser.partnerCode || editModalUser.id,
              createdAt: editModalUser.createdAt || new Date()
            });
            console.log('✅ Created in affiliates collection');
          } catch (createError) {
            console.log('Error creating in affiliates collection:', createError);
          }
        }
      }
      
      // Update email verification in Firebase Auth if needed
      if (userData.emailVerified !== editModalUser.emailVerified) {
        try {
          const updateEmailVerificationFunction = httpsCallable(functions, 'updateEmailVerification');
          await updateEmailVerificationFunction({
            uid: editModalUser.id,
            emailVerified: userData.emailVerified
          });
          console.log('✅ Email verification updated in Firebase Auth');
        } catch (error) {
          console.log('Could not update email verification in Firebase Auth:', error);
        }
      }
      
      alert('Felhasználó adatai sikeresen frissítve!');
      setEditModalUser(null);
      await fetchAllUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Hiba történt a felhasználó frissítésekor: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle user deletion with Firebase Auth integration
  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Biztosan törölni szeretnéd ${userName} felhasználót? Ez a művelet nem visszavonható és törli a Firebase Authentication fiókot is!`)) {
      return;
    }
    
    try {

      // Delete from Firebase Auth using admin function
      try {
        const deleteUserFunction = httpsCallable(functions, 'deleteUser');
        await deleteUserFunction({ uid: userId });
        console.log('✅ Deleted from Firebase Auth');
      } catch (authError) {
        console.log('Error deleting from Firebase Auth:', authError);
        // Continue with Firestore deletion
      }


      // Delete from users collection
      try {
        await deleteDoc(doc(db, 'users', userId));
        console.log('✅ Deleted from users collection');
      } catch (error) {
        console.log('User not in users collection');
      }

      // Delete from affiliates collection
      try {
        await deleteDoc(doc(db, 'affiliates', userId));
        console.log('✅ Deleted from affiliates collection');
      } catch (error) {
        console.log('User not in affiliates collection');
      }

      alert('Felhasználó sikeresen törölve!');
      await fetchAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Hiba történt a felhasználó törlésekor: ' + error.message);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('A jelszónak legalább 6 karakter hosszúnak kell lennie!');
      return;
    }

    setPasswordLoading(true);
    try {
      const changePasswordFunction = httpsCallable(functions, 'changeUserPassword');
      await changePasswordFunction({ 
        uid: passwordModalUser.id, 
        newPassword: newPassword 
      });
      
      alert('Jelszó sikeresen megváltoztatva!');
      setPasswordModalUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Hiba történt a jelszó változtatásakor: ' + error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Send password reset email
  const handleSendPasswordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Jelszó visszaállító email elküldve!');
    } catch (error) {
      console.error('Error sending password reset:', error);
      alert('Hiba történt az email küldésekor: ' + error.message);
    }
  };

  // Handle create new user
  const handleCreateUser = async () => {
    if (!createFormData.name || !createFormData.email || !createFormData.password || !createFormData.phone) {
      alert('Név, email, jelszó és telefonszám kötelező!');
      return;
    }

    if (createFormData.password.length < 6) {
      alert('A jelszónak legalább 6 karakter hosszúnak kell lennie!');
      return;
    }

    setCreateLoading(true);
    try {
      // Telefonszám formázás
      const formatPhoneNumber = (phone) => {
        if (!phone) return phone;
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('06')) {
          return '+36' + cleaned.substring(2);
        }
        if (cleaned.startsWith('36')) {
          return '+' + cleaned;
        }
        if (!cleaned.startsWith('36')) {
          return '+36' + cleaned;
        }
        return '+' + cleaned;
      };

      const formattedPhone = formatPhoneNumber(createFormData.phone);
      
      // Create user via Firebase Function
      const createUserFunction = httpsCallable(functions, 'createUserByAdmin');
      const result = await createUserFunction({
        email: createFormData.email,
        password: createFormData.password,
        name: createFormData.name,
        phone: formattedPhone,
        role: createFormData.role,
        isActive: createFormData.isActive,
        emailVerified: createFormData.emailVerified,
        balance: parseFloat(createFormData.balance) || 0
      });

      if (result.data.success) {
        alert('Felhasználó sikeresen létrehozva!');
        setCreateModalOpen(false);
        setCreateFormData({
          name: '',
          email: '',
          password: '',
          phone: '',
          role: 'user',
          isActive: true,
          emailVerified: false,
          balance: 0
        });
        await fetchAllUsers();
      } else {
        throw new Error(result.data.error || 'Ismeretlen hiba');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Hiba történt a felhasználó létrehozásakor: ' + error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = search === "" || 
      (user.name || user.fullName || '').toLowerCase().includes(search.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (user.partnerCode || user.referralCode || '').toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && user.isActive !== false) ||
      (statusFilter === "inactive" && user.isActive === false) ||
      (statusFilter === "admin" && user.role === "admin") ||
      (statusFilter === "affiliate" && user.role !== "admin");
    
    const matchesVerification = verificationFilter === "all" ||
      (verificationFilter === "verified" && user.emailVerified) ||
      (verificationFilter === "unverified" && !user.emailVerified);
    
    return matchesSearch && matchesStatus 
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? 'Bejelentkezés ellenőrzése...' : 'Felhasználók betöltése...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) return (
    <div className="text-red-600 text-center mt-10 p-4 bg-red-100 rounded-lg">
      {error}
      <button 
        onClick={fetchAllUsers}
        className="ml-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
      >
        Újrapróbálás
      </button>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto bg-white rounded-xl shadow-2xl p-6 mt-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900">Felhasználó Kezelés</h1>
          <p className="text-gray-600">Összes felhasználó: {users.length} | Szűrt: {filteredUsers.length}</p>
        </div>
        <button
          onClick={() => window.location.href = "/admin/dashboard"}
          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
        >
          Vissza a Dashboard-ra
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Keresés</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Név, email, kód..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Státusz</label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">Összes</option>
            <option value="active">Aktív</option>
            <option value="inactive">Inaktív</option>
            <option value="admin">Admin</option>
            <option value="affiliate">Affiliate</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Verifikáció</label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={verificationFilter}
            onChange={e => setVerificationFilter(e.target.value)}
          >
            <option value="all">Összes</option>
            <option value="verified">Megerősített</option>
            <option value="unverified">Nem megerősített</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Műveletek</label>
          <div className="space-y-2">
            <button
              onClick={fetchAllUsers}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium"
            >
              🔄 Újratöltés
            </button>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm font-medium"
            >
              ➕ Új felhasználó
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-4">
            <span className="font-medium text-yellow-800">
              {selectedUsers.length} felhasználó kiválasztva
            </span>
            <select
              className="border border-yellow-300 rounded-md px-3 py-1 text-sm"
              value={bulkAction}
              onChange={e => setBulkAction(e.target.value)}
            >
              <option value="">Válassz műveletet...</option>
              <option value="activate">Aktiválás</option>
              <option value="deactivate">Deaktiválás</option>
              <option value="verify">Email megerősítés</option>
              <option value="unverify">Email megerősítés visszavonása</option>
              <option value="delete">Törlés</option>
            </select>
            <button
              onClick={handleBulkAction}
              disabled={!bulkAction}
              className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-4 py-1 rounded-md text-sm font-medium"
            >
              Végrehajtás
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                  onChange={e => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Felhasználó</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kapcsolat</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kód</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Státusz</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regisztráció</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={e => handleUserSelect(user.id, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                </td>
                
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {(user.name || user.fullName || user.email || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || user.fullName || 'Névtelen'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.role === 'admin' ? '👑 Admin' : '👤 Affiliate'}
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <div className="text-sm text-gray-900">{user.email}</div>
                  <div className="text-sm text-gray-500">{user.phone || 'Nincs telefon'}</div>
                </td>
                
                <td className="px-4 py-4">
                  <div className="text-sm font-mono text-gray-900">
                    {user.partnerCode || user.referralCode || '-'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {user.source || 'users'}
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'admin' 
                      ? 'bg-purple-100 text-purple-800'
                      : user.isActive !== false 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                  }`}>
                    {user.role === 'admin' ? 'Admin' : user.isActive !== false ? 'Aktív' : 'Inaktív'}
                  </span>
                </td>
                
                <td className="px-4 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.emailVerified 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.emailVerified ? '✅ Megerősített' : '❌ Nem megerősített'}
                  </span>
                </td>
                
                <td className="px-4 py-4 text-sm text-gray-500">
                  {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('hu-HU') : 'Ismeretlen'}
                </td>
                
                <td className="px-4 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
                      title="Szerkesztés"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => setPasswordModalUser(user)}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs font-medium"
                      title="Jelszó változtatás"
                    >
                      🔑
                    </button>
                    <button
                      onClick={() => handleSendPasswordReset(user.email)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium"
                      title="Jelszó reset email"
                    >
                      📧
                    </button>
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name || user.fullName || user.email)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium"
                        title="Törlés"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Nincs megjeleníthető felhasználó</div>
          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setVerificationFilter("all");
            }}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Szűrők törlése
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editModalUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Felhasználó Szerkesztése</h2>
              <button
                onClick={() => setEditModalUser(null)}
                className="text-gray-500 hover:text-red-500 text-xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Név</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={editFormData.name || ''}
                  onChange={e => setEditFormData(prev => ({...prev, name: e.target.value}))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={editFormData.email || ''}
                  onChange={e => setEditFormData(prev => ({...prev, email: e.target.value}))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefonszám</label>
                <input
                  type="tel"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={editFormData.phone || ''}
                  onChange={e => setEditFormData(prev => ({...prev, phone: e.target.value}))}
                  placeholder="+36301234567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Szerepkör</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={editFormData.role || 'affiliate'}
                  onChange={e => setEditFormData(prev => ({...prev, role: e.target.value}))}
                >
                  <option value="user">User</option>
                  <option value="affiliate">Affiliate</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Egyenleg</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={editFormData.balance || 0}
                  onChange={e => setEditFormData(prev => ({...prev, balance: e.target.value}))}
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editFormData.isActive !== false}
                    onChange={e => setEditFormData(prev => ({...prev, isActive: e.target.checked}))}
                    className="rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Aktív felhasználó</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editFormData.emailVerified || false}
                    onChange={e => setEditFormData(prev => ({...prev, emailVerified: e.target.checked}))}
                    className="rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Email megerősített</span>
                </label>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => handleSaveUserEdit(editFormData)}
                disabled={editLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium"
              >
                {editLoading ? 'Mentés...' : 'Mentés'}
              </button>
              <button
                onClick={() => setEditModalUser(null)}
                disabled={editLoading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium"
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {passwordModalUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Jelszó Változtatás</h2>
              <button
                onClick={() => {
                  setPasswordModalUser(null);
                  setNewPassword('');
                }}
                className="text-gray-500 hover:text-red-500 text-xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Felhasználó: <strong>{passwordModalUser.name || passwordModalUser.email}</strong>
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Új jelszó</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 karakter"
                  minLength={6}
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleChangePassword}
                disabled={passwordLoading || !newPassword || newPassword.length < 6}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium"
              >
                {passwordLoading ? 'Változtatás...' : 'Jelszó Változtatás'}
              </button>
              <button
                onClick={() => {
                  setPasswordModalUser(null);
                  setNewPassword('');
                }}
                disabled={passwordLoading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium"
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Új Felhasználó Létrehozása</h2>
              <button
                onClick={() => {
                  setCreateModalOpen(false);
                  setCreateFormData({
                    name: '',
                    email: '',
                    password: '',
                    phone: '',
                    role: 'user',
                    isActive: true,
                    emailVerified: false,
                    balance: 0
                  });
                }}
                className="text-gray-500 hover:text-red-500 text-xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Név <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={createFormData.name}
                  onChange={e => setCreateFormData(prev => ({...prev, name: e.target.value}))}
                  placeholder="Teljes név"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={createFormData.email}
                  onChange={e => setCreateFormData(prev => ({...prev, email: e.target.value}))}
                  placeholder="email@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jelszó <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={createFormData.password}
                  onChange={e => setCreateFormData(prev => ({...prev, password: e.target.value}))}
                  placeholder="Minimum 6 karakter"
                  minLength={6}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefonszám <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={createFormData.phone}
                  onChange={e => setCreateFormData(prev => ({...prev, phone: e.target.value}))}
                  placeholder="+36301234567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Szerepkör</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={createFormData.role}
                  onChange={e => setCreateFormData(prev => ({...prev, role: e.target.value}))}
                >
                  <option value="user">User</option>
                  <option value="affiliate">Affiliate</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kezdő egyenleg</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={createFormData.balance}
                  onChange={e => setCreateFormData(prev => ({...prev, balance: e.target.value}))}
                  min="0"
                  step="0.01"
                  placeholder="0"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createFormData.isActive}
                    onChange={e => setCreateFormData(prev => ({...prev, isActive: e.target.checked}))}
                    className="rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Aktív felhasználó</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createFormData.emailVerified}
                    onChange={e => setCreateFormData(prev => ({...prev, emailVerified: e.target.checked}))}
                    className="rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Email megerősített</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="font-medium text-blue-900 mb-2">Szerepkör jogosultságok:</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <div><strong>User:</strong> Alapvető felhasználói jogok</div>
                  <div><strong>Affiliate:</strong> Ajánlói program hozzáférés</div>
                  <div><strong>Admin:</strong> Felhasználókezelés, moderálás</div>
                  <div><strong>Super Admin:</strong> Teljes rendszeradmin jogok</div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleCreateUser}
                disabled={createLoading || !createFormData.name || !createFormData.email || !createFormData.password || !createFormData.phone}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium"
              >
                {createLoading ? 'Létrehozás...' : 'Felhasználó Létrehozása'}
              </button>
              <button
                onClick={() => {
                  setCreateModalOpen(false);
                  setCreateFormData({
                    name: '',
                    email: '',
                    password: '',
                    phone: '',
                    role: 'user',
                    isActive: true,
                    emailVerified: false,
                    balance: 0
                  });
                }}
                disabled={createLoading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium"
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
